import React, { useState } from 'react';
import { useToast } from '../contexts/ToastContext';

interface ImageGeneratorProps {
  courseTitle: string;
  defaultPrompt?: string;
  platform?: string;
  onImageGenerated?: (imageUrl: string) => void;
  compact?: boolean;
}

const ImageGenerator: React.FC<ImageGeneratorProps> = ({
  courseTitle,
  defaultPrompt = '',
  platform = 'general',
  onImageGenerated,
  compact = false
}) => {
  const { showToast } = useToast();
  const [prompt, setPrompt] = useState(defaultPrompt);
  const [style, setStyle] = useState('professional');
  const [selectedPlatform, setSelectedPlatform] = useState(platform);
  const [generating, setGenerating] = useState(false);
  const [generatedImage, setGeneratedImage] = useState<string | null>(null);
  const [showInstructions, setShowInstructions] = useState(false);

  const styles = [
    { id: 'professional', name: 'Professional', desc: 'Clean, modern business style' },
    { id: 'vibrant', name: 'Vibrant', desc: 'Bold, colorful, eye-catching' },
    { id: 'minimal', name: 'Minimal', desc: 'Simple, elegant, lots of white space' },
    { id: 'creative', name: 'Creative', desc: 'Artistic, unique design' },
    { id: 'friendly', name: 'Friendly', desc: 'Warm, approachable atmosphere' }
  ];

  const platforms = [
    { id: 'instagram', name: 'Instagram', size: '1080x1080' },
    { id: 'facebook', name: 'Facebook', size: '1200x630' },
    { id: 'twitter', name: 'Twitter/X', size: '1600x900' },
    { id: 'linkedin', name: 'LinkedIn', size: '1200x627' },
    { id: 'pinterest', name: 'Pinterest', size: '1000x1500' },
    { id: 'youtube', name: 'YouTube Thumbnail', size: '1280x720' },
    { id: 'general', name: 'General/Square', size: '1024x1024' }
  ];

  const generateImage = async () => {
    if (!prompt.trim()) {
      showToast('Please describe what you want in the image', 'error');
      return;
    }

    setGenerating(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/ai/generate-image`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            prompt,
            style,
            platform: selectedPlatform,
            courseTitle
          })
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to generate image');
      }

      const data = await response.json();
      setGeneratedImage(data.imageUrl);
      showToast('Image generated! Download it or edit in Canva.', 'success');

      if (onImageGenerated) {
        onImageGenerated(data.imageUrl);
      }
    } catch (error: any) {
      console.error('Error generating image:', error);
      showToast(error.message || 'Failed to generate image', 'error');
    } finally {
      setGenerating(false);
    }
  };

  const downloadImage = async () => {
    if (!generatedImage) return;

    try {
      const response = await fetch(generatedImage);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${courseTitle.replace(/[^a-z0-9]/gi, '-')}-${selectedPlatform}-image.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      showToast('Image downloaded!', 'success');
    } catch (error) {
      // Fallback: open in new tab
      window.open(generatedImage, '_blank');
      showToast('Opening image in new tab - right-click to save', 'info');
    }
  };

  const openInCanva = () => {
    if (!generatedImage) return;

    // Canva's image editor URL with the image
    // Users can upload the image to Canva to edit
    const canvaUrl = `https://www.canva.com/design/new?type=Doc`;
    window.open(canvaUrl, '_blank');
    showToast('Canva opened! Upload your downloaded image to edit it.', 'info');
  };

  // Compact mode for inline use
  if (compact && !generatedImage) {
    return (
      <div className="flex items-center gap-2">
        <button
          onClick={() => setShowInstructions(true)}
          className="text-purple-600 hover:text-purple-700 text-sm font-medium flex items-center gap-1"
        >
          <span>🎨</span> Generate Image
        </button>

        {showInstructions && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold">Generate Marketing Image</h3>
                <button
                  onClick={() => setShowInstructions(false)}
                  className="text-gray-500 hover:text-gray-700 text-2xl"
                >
                  &times;
                </button>
              </div>

              <ImageGeneratorContent
                prompt={prompt}
                setPrompt={setPrompt}
                style={style}
                setStyle={setStyle}
                selectedPlatform={selectedPlatform}
                setSelectedPlatform={setSelectedPlatform}
                styles={styles}
                platforms={platforms}
                generating={generating}
                generatedImage={generatedImage}
                generateImage={generateImage}
                downloadImage={downloadImage}
                openInCanva={openInCanva}
                courseTitle={courseTitle}
              />
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <ImageGeneratorContent
      prompt={prompt}
      setPrompt={setPrompt}
      style={style}
      setStyle={setStyle}
      selectedPlatform={selectedPlatform}
      setSelectedPlatform={setSelectedPlatform}
      styles={styles}
      platforms={platforms}
      generating={generating}
      generatedImage={generatedImage}
      generateImage={generateImage}
      downloadImage={downloadImage}
      openInCanva={openInCanva}
      courseTitle={courseTitle}
    />
  );
};

// Extracted content component for reuse
interface ContentProps {
  prompt: string;
  setPrompt: (p: string) => void;
  style: string;
  setStyle: (s: string) => void;
  selectedPlatform: string;
  setSelectedPlatform: (p: string) => void;
  styles: { id: string; name: string; desc: string }[];
  platforms: { id: string; name: string; size: string }[];
  generating: boolean;
  generatedImage: string | null;
  generateImage: () => void;
  downloadImage: () => void;
  openInCanva: () => void;
  courseTitle: string;
}

const ImageGeneratorContent: React.FC<ContentProps> = ({
  prompt,
  setPrompt,
  style,
  setStyle,
  selectedPlatform,
  setSelectedPlatform,
  styles,
  platforms,
  generating,
  generatedImage,
  generateImage,
  downloadImage,
  openInCanva,
  courseTitle
}) => {
  return (
    <div className="space-y-6">
      {/* Instructions Box */}
      <div className="bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-xl p-5">
        <h4 className="font-bold text-purple-800 mb-3 flex items-center gap-2">
          <span className="text-xl">🎨</span> How AI Image Generation Works
        </h4>
        <div className="space-y-2 text-sm text-purple-700">
          <p><strong>1. Describe your image:</strong> Tell the AI what you want to see (e.g., "A person working on a laptop with success charts in the background")</p>
          <p><strong>2. Choose a style:</strong> Pick a visual style that matches your brand</p>
          <p><strong>3. Select platform:</strong> Choose where you'll use the image for optimal sizing</p>
          <p><strong>4. Generate & Edit:</strong> Create your image, then download or edit in Canva to add text/branding</p>
        </div>
        <div className="mt-3 p-3 bg-white rounded-lg border border-purple-100">
          <p className="text-xs text-purple-600">
            <strong>Pro Tip:</strong> Images are generated WITHOUT text so you can add your own headlines,
            logos, and branding in Canva. This gives you full control over the final design!
          </p>
        </div>
      </div>

      {/* Image Prompt */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          Describe Your Image
        </label>
        <textarea
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          rows={3}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
          placeholder="Example: A confident professional teaching online, modern office background, warm lighting, educational atmosphere"
        />
        <p className="text-xs text-gray-500 mt-1">
          Be specific about colors, mood, objects, and setting. The more detail, the better!
        </p>
      </div>

      {/* Style Selection */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          Visual Style
        </label>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {styles.map((s) => (
            <button
              key={s.id}
              onClick={() => setStyle(s.id)}
              className={`p-3 rounded-lg border-2 text-left transition-all ${
                style === s.id
                  ? 'border-purple-500 bg-purple-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="font-medium text-sm">{s.name}</div>
              <div className="text-xs text-gray-500">{s.desc}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Platform Selection */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          Platform / Size
        </label>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {platforms.map((p) => (
            <button
              key={p.id}
              onClick={() => setSelectedPlatform(p.id)}
              className={`p-3 rounded-lg border-2 text-center transition-all ${
                selectedPlatform === p.id
                  ? 'border-purple-500 bg-purple-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="font-medium text-sm">{p.name}</div>
              <div className="text-xs text-gray-500">{p.size}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Generate Button */}
      <button
        onClick={generateImage}
        disabled={generating || !prompt.trim()}
        className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white py-4 rounded-xl font-bold text-lg hover:from-purple-700 hover:to-pink-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
      >
        {generating ? (
          <>
            <span className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full"></span>
            Generating Image...
          </>
        ) : (
          <>
            <span>🎨</span> Generate Image
          </>
        )}
      </button>

      {/* Generated Image Display */}
      {generatedImage && (
        <div className="space-y-4">
          <div className="border-2 border-green-200 rounded-xl p-4 bg-green-50">
            <h4 className="font-bold text-green-800 mb-3 flex items-center gap-2">
              <span>✅</span> Your Image is Ready!
            </h4>

            <div className="bg-white rounded-lg p-2 mb-4">
              <img
                src={generatedImage}
                alt="Generated marketing image"
                className="w-full rounded-lg shadow-md"
              />
            </div>

            {/* Action Buttons */}
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={downloadImage}
                className="bg-blue-600 text-white py-3 px-4 rounded-lg font-semibold hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
              >
                <span>⬇️</span> Download Image
              </button>
              <button
                onClick={openInCanva}
                className="bg-gradient-to-r from-cyan-500 to-blue-500 text-white py-3 px-4 rounded-lg font-semibold hover:from-cyan-600 hover:to-blue-600 transition-colors flex items-center justify-center gap-2"
              >
                <span>🎨</span> Edit in Canva
              </button>
            </div>

            {/* Next Steps */}
            <div className="mt-4 p-4 bg-white rounded-lg border border-green-200">
              <h5 className="font-semibold text-gray-800 mb-2">Next Steps:</h5>
              <ol className="text-sm text-gray-600 space-y-1 list-decimal list-inside">
                <li>Download the image OR click "Edit in Canva"</li>
                <li>In Canva: Upload image → Add your headline text</li>
                <li>Add your logo and brand colors</li>
                <li>Resize for different platforms if needed</li>
                <li>Download and post to social media!</li>
              </ol>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageGenerator;
